import { useCallback, useEffect, useRef, useState } from 'react';
import { PencilSimple, Trash, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import ItemPickerField from '@/pages/mfg/ItemPickerField';
import { mktApi, errMsg } from './marketingApi';
import { jd, yen } from './mktShared';

const EMPTY = { item_id: null, item: null, unit_price: '', valid_from: '', valid_to: '', notes: '' };

export default function PriceListDialog({ open, onOpenChange, customer }) {
  const [prices, setPrices] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const csvRef = useRef(null);

  const load = useCallback(async () => {
    if (!customer) return;
    try { setPrices(await mktApi.priceList(customer.id)); }
    catch (e) { toast.error(errMsg(e, '単価の取得に失敗しました')); }
  }, [customer]);

  useEffect(() => { if (open) { load(); setF(EMPTY); setEditingId(null); setImportResult(null); } }, [open, load]);

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await mktApi.importPrices(customer.id, file);
      setImportResult(res);
      if (res.added > 0) toast.success(`${res.added}件の単価を取り込みました`);
      if (res.error_count > 0) toast.error(`${res.error_count}件のエラーがあります`);
      load();
    } catch (e) { toast.error(errMsg(e, 'CSV取り込みに失敗しました')); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ''; }
  };

  const save = async () => {
    if (!f.item_id) { toast.error('品目を選択してください'); return; }
    if (f.unit_price === '' || Number(f.unit_price) < 0) { toast.error('単価を入力してください'); return; }
    setSaving(true);
    try {
      const body = {
        customer_id: customer.id, item_id: f.item_id, unit_price: Number(f.unit_price),
        valid_from: f.valid_from || null, valid_to: f.valid_to || null, notes: f.notes || null,
      };
      if (editingId) await mktApi.updatePrice(editingId, body);
      else await mktApi.createPrice(body);
      toast.success(editingId ? '単価を更新しました' : '単価を登録しました');
      setF(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setF({
      item_id: p.item_id, item: { id: p.item_id, name: p.item_name, sku: p.item_sku, uom: p.item_uom },
      unit_price: String(p.unit_price), valid_from: p.valid_from || '', valid_to: p.valid_to || '', notes: p.notes || '',
    });
  };

  const remove = async (p) => {
    if (!window.confirm(`「${p.item_name}」の単価を削除しますか？`)) return;
    try { await mktApi.deletePrice(p.id); toast.success('単価を削除しました'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mkt-price-list-dialog">
        <DialogHeader><DialogTitle>販売単価マスタ — {customer?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <p className="flex-1 rounded-lg bg-fuchsia-50 border border-fuchsia-100 px-3 py-2 text-[11px] text-fuchsia-700">
              登録した単価は、この客先の見積計算で自動適用されます（手入力の単価が優先されます）。
            </p>
            <div className="shrink-0">
              <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden"
                onChange={(e) => importCsv(e.target.files?.[0])} data-testid="mkt-price-csv-input" />
              <Button variant="outline" size="sm" disabled={importing} onClick={() => csvRef.current?.click()}
                data-testid="mkt-price-csv-import-btn">
                <UploadSimple size={14} className="mr-1.5" /> {importing ? '取込中…' : 'CSV取り込み'}
              </Button>
              <p className="mt-1 text-[9px] text-zinc-400 text-right">列: 品番, 単価, 適用開始, 適用終了, 備考</p>
            </div>
          </div>
          {importResult && importResult.error_count > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-600 max-h-28 overflow-y-auto" data-testid="mkt-price-import-errors">
              <div className="font-semibold mb-0.5">取込: {importResult.added}件成功 / {importResult.error_count}件エラー</div>
              {importResult.errors.map((er, i) => <div key={i}>{er.row}行目: {er.message}</div>)}
            </div>
          )}

          <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-12 sm:col-span-4">
                <label className="text-xs font-medium text-zinc-600">品目 <span className="text-red-500">*</span></label>
                <div className="mt-1">
                  <ItemPickerField value={f.item_id} allowAllDepartments defaultAllMode
                    onChange={(id, item) => setF((p) => ({ ...p, item_id: id, item }))}
                    testid="mkt-price-item" placeholderKey="mfg.wo.selectProduct" />
                </div>
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="text-xs font-medium text-zinc-600">単価 (円) <span className="text-red-500">*</span></label>
                <Input type="number" min="0" step="any" value={f.unit_price}
                  onChange={(e) => setF((p) => ({ ...p, unit_price: e.target.value }))}
                  className="mt-1 h-10 text-right" data-testid="mkt-price-unit-price" />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="text-xs font-medium text-zinc-600">適用開始</label>
                <Input type="date" value={f.valid_from} onChange={(e) => setF((p) => ({ ...p, valid_from: e.target.value }))}
                  className="mt-1 h-10" data-testid="mkt-price-valid-from" />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="text-xs font-medium text-zinc-600">適用終了</label>
                <Input type="date" value={f.valid_to} onChange={(e) => setF((p) => ({ ...p, valid_to: e.target.value }))}
                  className="mt-1 h-10" data-testid="mkt-price-valid-to" />
              </div>
              <div className="col-span-12 sm:col-span-2 flex gap-1.5">
                <Button onClick={save} disabled={saving} className="h-10 flex-1 bg-teal-700 hover:bg-teal-800" data-testid="mkt-price-save-btn">
                  {saving ? '保存中…' : (editingId ? '更新' : '追加')}
                </Button>
                {editingId && (
                  <Button variant="outline" className="h-10" onClick={() => { setEditingId(null); setF(EMPTY); }} data-testid="mkt-price-cancel-edit-btn">取消</Button>
                )}
              </div>
              <div className="col-span-12">
                <Input value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="備考（任意）" className="h-9" data-testid="mkt-price-notes" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] text-zinc-400">
                  <th className="px-3 py-2 font-medium">品名 / 品番</th>
                  <th className="px-2 py-2 font-medium text-right">単価</th>
                  <th className="px-2 py-2 font-medium">適用期間</th>
                  <th className="px-2 py-2 font-medium">備考</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {prices.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-zinc-400" data-testid="mkt-price-list-empty">単価が登録されていません</td></tr>
                )}
                {prices.map((p) => (
                  <tr key={p.id} className={`border-b border-zinc-50 ${editingId === p.id ? 'bg-fuchsia-50/50' : ''}`} data-testid={`mkt-price-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-zinc-800"><span data-no-translate>{p.item_name}</span></div>
                      {p.item_sku && <div className="font-mono text-[10px] text-zinc-400">{p.item_sku}</div>}
                    </td>
                    <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums">{yen(p.unit_price)}<span className="ml-0.5 font-normal text-zinc-400">/{p.item_uom || '個'}</span></td>
                    <td className="px-2 py-2 text-xs tabular-nums text-zinc-600">
                      {p.valid_from || p.valid_to ? `${jd(p.valid_from) === '—' ? '' : jd(p.valid_from)} 〜 ${jd(p.valid_to) === '—' ? '' : jd(p.valid_to)}` : '無期限'}
                    </td>
                    <td className="px-2 py-2 text-xs text-zinc-500">{p.notes || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button type="button" onClick={() => edit(p)} className="text-zinc-400 hover:text-fuchsia-600 transition-colors mr-3" data-testid={`mkt-price-edit-${p.id}`}><PencilSimple size={15} /></button>
                      <button type="button" onClick={() => remove(p)} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`mkt-price-delete-${p.id}`}><Trash size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
